import React, { useState, useEffect, useCallback } from 'react';
import { client } from '../utils/api';

const CONTROLLER = 'eventRegistrationController.php';
const EVENTS_CONTROLLER = 'eventsController.php';
const USERS_CONTROLLER = 'usersController.php';
const BASE_URL = process.env.REACT_APP_DB_BASE_URL || 'http://localhost/api';
const formatCHF = (amount) => new Intl.NumberFormat('en-CH', {
  style: 'currency',
  currency: 'CHF',
}).format(Number(amount) || 0);

export default function EventRegistrationsManager({ onSendEmail = () => {} }) {
  const [events, setEvents] = useState([]);
  const [collectedByEvent, setCollectedByEvent] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserToAdd, setSelectedUserToAdd] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [amountPaidToAdd, setAmountPaidToAdd] = useState('');
  const [amountPreset, setAmountPreset] = useState('custom');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadEvents = useCallback(async () => {
    try {
      const res = await client.get(EVENTS_CONTROLLER);
      setEvents(res.events || []);
      if (!selectedEventId && (res.events || []).length) {
        setSelectedEventId(String(res.events[0].id));
      }
    } catch (err) {
      setError('Failed to fetch events: ' + err.message);
    }
  }, [selectedEventId]);

  const loadUsers = useCallback(async () => {
    try {
      const res = await client.get(USERS_CONTROLLER);
      setUsers(res.users || []);
    } catch (err) {
      setError((previous) => previous || 'Failed to fetch users: ' + err.message);
    }
  }, []);

  const loadCollectedAmounts = useCallback(async () => {
    try {
      const result = await client.get(CONTROLLER);
      const totals = {};
      (result.registrations || []).forEach((registration) => {
        const eventId = registration.eventId ?? registration.event_id;
        const amountPaid = Number(registration.amountPaid ?? registration.amount_paid ?? 0);
        if (eventId !== undefined && Number.isFinite(amountPaid)) {
          totals[eventId] = (totals[eventId] || 0) + amountPaid;
        }
      });
      setCollectedByEvent(totals);
    } catch (err) {
      setError((previous) => previous || 'Failed to fetch collected amounts: ' + err.message);
    }
  }, []);

  const loadRegistrationsForEvent = useCallback(async (eventId) => {
    try {
      const response = await fetch(`${BASE_URL}/${CONTROLLER}?eventId=${eventId}`, {
        method: 'GET', // or POST depending on your endpoint
        credentials: 'include' // MANDATORY to attach the PHPSESSID cookie
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }
      setRegistrations(result.registrations || []);
    } catch (err) {
      setError('Failed to fetch registrations: ' + err.message);
    }
  }, []);

  useEffect(() => {
    loadEvents();
    loadUsers();
    loadCollectedAmounts();
  }, [loadEvents, loadUsers, loadCollectedAmounts]);

  useEffect(() => {
    if (!selectedEventId) {
      setRegistrations([]);
      return;
    }

    loadRegistrationsForEvent(selectedEventId);
  }, [selectedEventId, loadRegistrationsForEvent]);

  const selectedEvent = events.find((event) => String(event.id) === String(selectedEventId)) || null;

  const availableUsersForEvent = users.filter((user) => {
    const isAlreadyRegistered = registrations.some(
      (registration) => Number(registration.user_id) === Number(user.id)
    );

    if (isAlreadyRegistered) {
      return false;
    }

    const query = userSearchTerm.trim().toLowerCase();
    if (!query) {
      return false;
    }

    const searchableText = `${user.fullName || ''} ${user.email || ''}`.toLowerCase();
    return searchableText.includes(query);
  });

  const handleSendEmail = () => {
    if (!selectedEvent) {
      setError('Choose an event before sending email.');
      return;
    }

    const recipients = Array.from(
      new Set(
        registrations
          .map((registration) => registration.email)
          .filter(Boolean)
      )
    );

    if (recipients.length === 0) {
      setError('No attendee emails are available for this event.');
      return;
    }

    setError('');
    onSendEmail({
      recipients,
      subject: `RE ${selectedEvent.title}`,
    });
  };

  const handleAddUser = async (
    userIdToAdd = selectedUserId,
    amountOverride = amountPaidToAdd,
    registrationStatusOverride = amountPreset === 'unpaid' ? 'unpaid' : 'registered'
  ) => {
    const targetUserId = Number(userIdToAdd);
    if (!selectedEventId || !targetUserId) {
      setError('Choose an event and a user before adding a registration.');
      return;
    }

    const parsedAmount = Number(amountOverride ?? amountPaidToAdd ?? 0);
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setError('Amount paid must be a valid non-negative number.');
      return;
    }

    const normalizedStatus = String(registrationStatusOverride || 'registered').trim().toLowerCase();
    const finalStatus = normalizedStatus === 'unpaid' ? 'unpaid' : 'registered';

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${BASE_URL}/${CONTROLLER}`, {
        method: 'POST',
        credentials: 'include', // MANDATORY to attach the PHPSESSID cookie
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: Number(selectedEventId),
          userId: targetUserId,
          amount: parsedAmount,
          amountPaid: parsedAmount,
          amount_paid: parsedAmount,
          paidAmount: parsedAmount,
          paymentAmount: parsedAmount,
          registrationStatus: finalStatus,
          registration_status: finalStatus,
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      setSelectedUserId('');
      setSelectedUserToAdd(null);
      setUserSearchTerm('');
      setAmountPaidToAdd('');
      setAmountPreset('custom');
      setSuccess('User added to the event.');
      await loadRegistrationsForEvent(selectedEventId);
      await loadCollectedAmounts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRegistration = async (userId) => {
    if (!selectedEventId) return;

    if (!window.confirm('Remove this user from the event?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      const response = await fetch(`${BASE_URL}/${CONTROLLER}?eventId=${selectedEventId}&userId=${userId}`, {
        method: 'DELETE',
        credentials: 'include', // MANDATORY to attach the PHPSESSID cookie
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }
      setSuccess('User removed from the event.');
      await loadRegistrationsForEvent(selectedEventId);
      await loadCollectedAmounts();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-view-wrapper">
      <div className="admin-view-header">
        <h2 className="admin-title">Event Registrations</h2>
        {error && <div className="admin-error">{error}</div>}
        {success && <div className="admin-success">{success}</div>}
      </div>

      <div className="admin-registration-layout">
        <aside className="admin-event-list-panel">
          <div className="panel-header">
            <h3>Events</h3>
          </div>

          {events.length === 0 ? (
            <div className="no-records">No events found.</div>
          ) : (
            events.map((event) => (
              <button
                key={event.id}
                type="button"
                className={`event-select-card ${String(event.id) === String(selectedEventId) ? 'active' : ''}`}
                onClick={() => setSelectedEventId(String(event.id))}
              >
                <strong>{event.title}</strong>
                <span>{event.location || 'Location not set'}</span>
                <small>{event.startTime ? new Date(event.startTime).toLocaleString() : 'No date'}</small>
                <span className="event-collected-total">
                  <span>Collected</span>
                  <strong>{formatCHF(collectedByEvent[event.id] || 0)}</strong>
                </span>
              </button>
            ))
          )}
        </aside>

        <section className="admin-registration-detail-panel">
          {!selectedEvent ? (
            <div className="no-records">Select an event to manage registrations.</div>
          ) : (
            <>
              <div className="panel-header">
                <h3>{selectedEvent.title}</h3>
              </div>

              <div className="event-registration-meta">
                <span>{selectedEvent.location || 'No location'}</span>
                <span>{selectedEvent.startTime ? new Date(selectedEvent.startTime).toLocaleString() : 'No date'}</span>
              </div>

              <div className="admin-form-container registration-add-form">
                <div className="form-group">
                  <label>Add user to this event</label>
                  <input
                    type="text"
                    placeholder="Search by name or email"
                    value={userSearchTerm}
                    onChange={(e) => {
                      setUserSearchTerm(e.target.value);
                      setSelectedUserId('');
                    }}
                  />
                </div>

                <div className="registration-email-actions">
                  <button type="button" className="btn btn-submit-professionals" onClick={handleSendEmail}>
                    Send email to all attendees
                  </button>
                </div>

                {userSearchTerm.trim() && (
                  <div className="user-search-results">
                    {availableUsersForEvent.length === 0 ? (
                      <div className="no-records">No matching users available.</div>
                    ) : (
                      availableUsersForEvent.slice(0, 12).map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          className="user-search-result"
                          onClick={() => {
                            setSelectedUserToAdd(user);
                            setSelectedUserId(String(user.id));
                            const memberPrice = Number(selectedEvent?.memberPrice ?? selectedEvent?.member_price ?? 0);
                            const nonMemberPrice = Number(selectedEvent?.nonMemberPrice ?? selectedEvent?.non_member_price ?? 0);
                            setAmountPreset(memberPrice > 0 ? 'member' : 'non-member');
                            setAmountPaidToAdd(String(memberPrice > 0 ? memberPrice : nonMemberPrice));
                          }}
                        >
                          <strong>{user.fullName || 'Unknown user'}</strong>
                          <span>{user.email}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}

                {selectedUserToAdd && (
                  <div className="admin-form-container" style={{ marginTop: '16px', padding: '16px' }}>
                    <div className="form-group">
                      <label>Selected user</label>
                      <div style={{ fontWeight: 600 }}>{selectedUserToAdd.fullName || 'Unknown user'}</div>
                      <div style={{ opacity: 0.8 }}>{selectedUserToAdd.email}</div>
                    </div>

                    <div className="form-group">
                      <label>Recommended amount</label>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {[
                          { key: 'member', label: 'Member', value: Number(selectedEvent?.memberPrice ?? selectedEvent?.member_price ?? 0) },
                          { key: 'non-member', label: 'Non-member', value: Number(selectedEvent?.nonMemberPrice ?? selectedEvent?.non_member_price ?? 0) },
                          { key: 'unpaid', label: 'Register unpaid', value: 0 },
                        ].map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            className="btn btn-secondary"
                            style={{
                              minWidth: '132px',
                              opacity: amountPreset === option.key ? 1 : 0.75,
                              border: amountPreset === option.key ? '1px solid #d4af37' : '1px solid #d9d9d9',
                            }}
                            onClick={() => {
                              setAmountPreset(option.key);
                              setAmountPaidToAdd(String(option.value));
                            }}
                          >
                            {option.label}: {option.value === 0 ? 'CHF 0' : `CHF ${option.value}`}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="custom-amount-paid">Custom amount</label>
                      <input
                        id="custom-amount-paid"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={amountPaidToAdd}
                        onChange={(e) => {
                          setAmountPreset('custom');
                          setAmountPaidToAdd(e.target.value);
                        }}
                        placeholder="0.00"
                      />
                    </div>

                    <button
                      type="button"
                      className="btn btn-submit-professionals"
                      onClick={() => handleAddUser(selectedUserToAdd.id, amountPaidToAdd, amountPreset === 'unpaid' ? 'unpaid' : 'registered')}
                    >
                      Add to event
                    </button>
                  </div>
                )}
              </div>

              <div className="admin-table-section">
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr className="table-header-row">
                        <th className="table-header-cell">User</th>
                        <th className="table-header-cell">Email</th>
                        <th className="table-header-cell">Status</th>
                        <th className="table-header-cell">Amount Paid</th>
                        <th className="table-header-cell">Registered</th>
                        <th className="table-header-cell table-header-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="table-body-cell">No users registered for this event.</td>
                        </tr>
                      ) : (
                        registrations.map((registration) => (
                          <tr key={registration.id} className="table-body-row">
                            <td className="table-body-cell">{registration.full_name || 'Unknown user'}</td>
                            <td className="table-body-cell">{registration.email || '—'}</td>
                            <td className="table-body-cell">{registration.registration_status || 'unpaid'}</td>
                            <td className="table-body-cell">{formatCHF(registration.amount_paid ?? registration.amountPaid)}</td>
                            <td className="table-body-cell">{registration.registered_at ? new Date(registration.registered_at).toLocaleString() : '—'}</td>
                            <td className="table-body-cell table-body-actions">
                              <button
                                type="button"
                                className="btn-action-delete"
                                onClick={() => handleDeleteRegistration(registration.user_id)}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
